<template>
  <div id="top" class="claim-registry-page claim-page-shell care-billing-page-shell">
    <div class="claim-registry-intro">
      <UiPageHeader title="Billing Batches" description="Review visit batches and submit claims when ready.">
        <template #actions>
          <button type="button" class="claim-add-link billing-start-action" :class="{ 'is-cancel': showStartPanel }" :disabled="!hasManualBillingSchemes" :title="hasManualBillingSchemes ? 'Start Batch' : 'No manual billing scheme is enabled'" @click="handlePrimaryStartAction">
            <X v-if="showStartPanel" :size="17" aria-hidden="true" />
            <FilePlus2 v-else :size="17" aria-hidden="true" />
            <span>{{ showStartPanel ? 'Cancel' : 'Start Batch' }}</span>
          </button>
        </template>
      </UiPageHeader>
    </div>

    <section class="claim-workbench sba-slide-in" aria-label="Provider billing register">
      <div id="batch-start" v-if="showStartPanel">
        <StartBillingBatch @started="handleBatchStarted" @cancel="handleStartCancel" />
      </div>
      <div class="claim-registry-toolbar" v-if="!showStartPanel">
        <div class="claim-review-summary" aria-live="polite">
          <strong v-if="isReady">{{ clusters.length }} {{ clusters.length === 1 ? 'batch' : 'batches' }} in this view</strong>
          <strong v-else>Loading billing batches</strong>
          <span>Provider visit batches and claims submission</span>
        </div>
        <button type="button" class="claim-search-toggle" aria-controls="batch-filter" :aria-expanded="showSearchFilters" @click="showSearchFilters = !showSearchFilters">
          <span>{{ showSearchFilters ? 'Close filters' : 'Filter' }}</span>
          <i class="material-icons" aria-hidden="true">{{ showSearchFilters ? 'close' : 'filter_list' }}</i>
        </button>
      </div>
      <div id="batch-filter" class="claim-filter-shell" v-show="showSearchFilters && !showStartPanel">
        <div class="claim-filter-panel"><FilterBilling @search="handleSearch" /></div>
      </div>
      <div class="claim-search-summary" v-if="!showStartPanel">
        <span class="summary-label">Current view</span>
        <div class="summary-content" v-if="searchTitle" v-html="searchTitle"></div>
        <div class="summary-content" v-else><span class="summary-chip">All billing batches</span></div>
      </div>
      <div id="review"></div>
      <LoadingDiv v-if="!isReady && !showStartPanel" />
      <div v-else v-show="!showStartPanel">
        <section class="claim-cluster-section" v-if="clusters.length || isLoading" aria-label="Billing batches">
          <div class="claim-cluster-list">
            <div class="claim-list-progress" v-if="isLoading" aria-label="Refreshing billing batches"><span></span></div>
            <div class="claim-cluster-head" aria-hidden="true">
              <span>No.</span><span>Batch (scheme)</span><span>Encounters</span><span>Sent</span><span>Stage</span><span>Payment</span><span></span>
            </div>
            <article class="claim-cluster-row" v-for="(cluster, index) in clusters" :key="cluster.id">
              <div class="claim-row-number">{{ numberBadge(index) }}</div>
              <div class="claim-provider-cell">
                <div class="claim-provider-title">{{ toMonthYear(cluster.month_year) }} Claims</div>
                <span class="claim-count-text">{{ HMO }} {{ cluster.scheme.name }}</span>
              </div>
              <div class="claim-data-cell">
                <span class="claim-mobile-label">Encounters</span>
                <span>{{ cluster.encounters.length }} {{ cluster.encounters.length === 1 ? 'visit' : 'visits' }}</span>
              </div>
              <div class="claim-data-cell">
                <span class="claim-mobile-label">Sent</span>
                <span class="claim-status is-unsubmitted" v-if="cluster.status === 'q'">Unsubmitted</span>
                <span class="claim-amount is-sent" v-else>&#8358;{{ grossTotalSent(cluster.encounters) }}</span>
              </div>
              <div class="claim-data-cell">
                <span class="claim-mobile-label">Stage</span>
                <span class="claim-status is-pending" v-if="cluster.status === 'p'">Pending</span>
                <span class="claim-muted-value" v-if="cluster.status === 'q'">N/A</span>
                <span class="claim-status is-paid" v-if="cluster.status === 'r' || cluster.status === 'v'">Completed</span>
              </div>
              <div class="claim-data-cell">
                <span class="claim-mobile-label">Payment</span>
                <span class="claim-status" v-if="cluster.status === 'r' || cluster.status === 'v'" :class="{
                  'is-paid': paymentStatus(cluster)?.toLowerCase() === 'paid',
                  'is-pending': paymentStatus(cluster)?.toLowerCase() === 'pending',
                }">{{ checkPaid(cluster) ? 'Paid' : 'Pending' }}</span>
                <span class="claim-muted-value" v-else>N/A</span>
              </div>
              <div class="claim-action-cell">
                <button type="button" class="claim-row-action" :aria-label="cluster.status === 'q' ? 'Review batch' : 'Open batch details'" :title="cluster.status === 'q' ? 'Review batch' : 'Open batch details'" @click="handleReview(cluster)">
                  <i class="material-icons" aria-hidden="true">{{ cluster.status === 'q' ? 'edit' : 'chevron_right' }}</i>
                </button>
              </div>
            </article>
          </div>
        </section>
        <div class="section claim-empty-state" v-if="!clusters.length && !isLoading">
          <div class="claim-empty-icon"><i class="material-icons" aria-hidden="true">receipt_long</i></div>
          <div class="claim-empty-copy">
            <h6>No billing batches found</h6>
            <p>There are no provider billing clusters matching the current view.</p>
            <span>Try another period or billing status filter.</span>
          </div>
        </div>
        <div class="claim-pagination" v-else>
          <PaginationDiv :payLoad="allPages" @newPage="handlePage" />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import getItemsOfItems from "@/composables/getItemsOfItems";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import { FilePlus2, X } from "@lucide/vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import FilterBilling from "@/components/billing/FilterBillingComponent.vue";
import StartBillingBatch from "@/components/billing/StartBillingBatchComponent.vue";
import {
    capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import {
    grossTotalSentFx,
    grossTotalApprovedFx,
    checkPaidFx,
    paymentStatusFx,
} from "@/composables/calFunction/cal";
import { getCache, addCache } from "@/composables/cache/cache";
import { HMO } from '@/laravel/config';


export default {
    name: "billingHome",
    components: { FilePlus2, X, UiPageHeader, LoadingDiv, PaginationDiv, FilterBilling, StartBillingBatch },
    setup() {
        const response = ref(null);
        const isReady = ref(false);
        const isLoading = ref(false);
        const error = ref(null);
        const currUrl = ref(null);
        const currHook = ref(null);
        const searchTitle = ref(null);
        const home = ref(null);
        const currTab = ref(null);
        const tabs = ref([]);
        const UIUpdate = ref(true);
        const showSearchFilters = ref(false);
        const showStartPanel = ref(false);

        // vue router
        const route = useRoute();
        const router = useRouter();

        // store
        const store = useStore();

        // config
        const providerId = store.state?.user?.account?.id;
        const isManualScheme = (scheme) => {
            return scheme?.is_manual === true || scheme?.is_manual === 1 || scheme?.is_manual === "1";
        };
        const hasManualBillingSchemes = computed(() => {
            return (store.state?.user?.schemes || []).some(isManualScheme);
        });

        // start
        onMounted(() => {
            // page name
            document.title = "All Claims Cluster";

            if (!route.hash) {
                resetBillingHomeScroll();
            }
        });

        // menu
        onMounted(() => {
            // load tab
            tabs.value.push({ id: 1, name: "Billing", title: "All", icon: null, url: "billing", hash: null, });
            tabs.value.push({ id: 2, name: "Billing", title: "Review", icon: "edit", url: "billing", hash: "#review" });

            if (hasManualBillingSchemes.value) {
                tabs.value.push({ id: 3, name: "Billing", title: "Start", icon: "add", url: "billing", hash: "#batch-start" });
            }

            // set default
            currTab.value = tabs.value.find(x => x.title.includes("All"));
            // set home
            home.value = tabs.value.find(x => x.title.includes("All"));

            if (route.hash === "#batch-start" && hasManualBillingSchemes.value) {
                openStartPanel();
            }
        });

        // handle curr status
        const handleActive = (tab) => {
            currTab.value = tab;
            if (tab.hash === "#batch-start") {
                if (!hasManualBillingSchemes.value) {
                    router.push({ name: "billing" });
                    return;
                }

                openStartPanel();
                return;
            }
            showStartPanel.value = false;
            showSearchFilters.value = true;
            // refer
            if (tab.hash) {
                router.push({ hash: tab.hash });
            } else {
                router.push({ name: tab.url });
            }
        };

        // numbering
        const numberBadge = (num) => {
            return (
                (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
            );
        };

        //from functions
        const capWords = (val) => {
            return capWordsFx(val);
        };

        // total calculations (gross sent)
        const grossTotalSent = (encounters) => {
            return grossTotalSentFx(encounters);
        };

        // total calculations (gross approved)
        const grossTotalApproved = (encounters, db) => {
            return grossTotalApprovedFx(encounters, db);
        };

        // convert to Month & Year
        const toMonthYear = (val) => {
            let parts = val.split("_");
            let month = parseInt(parts[0]);
            let year = parseInt(parts[1]);
            let date = new Date(year, month - 1); // months are zero-indexed in JavaScript
            let monthName = date.toLocaleString("default", { month: "short" });
            return monthName + " " + year;
        };

        // payment status
        const checkPaid = (payment, repayment) => {
            return checkPaidFx(payment, repayment);
        };

        // payment status
        const paymentStatus = (cluster) => {
            return paymentStatusFx(cluster);
        };

        const focusBatchFilters = () => {
            showSearchFilters.value = true;
            router.push({ hash: "#batch-filter" });
        };

        const handlePrimaryStartAction = () => {
            if (!hasManualBillingSchemes.value) {
                return;
            }

            if (showStartPanel.value) {
                handleStartCancel();
            } else {
                openStartPanel();
            }
        };

        const handleStartCancel = () => {
            showStartPanel.value = false;
            showSearchFilters.value = true;
            currTab.value = tabs.value.find(x => x.title.includes("All"));
            router.push({ name: "billing" });
        };

        const handleBatchStarted = (cluster) => {
            showStartPanel.value = false;
            router.push({ name: "billingView", params: { clusterId: cluster.id } });
        };

        const openStartPanel = () => {
            showStartPanel.value = true;
            showSearchFilters.value = false;
            currTab.value = tabs.value.find(x => x.title.includes("Start"));
            window.history.replaceState(null, "", "#batch-start");
            scrollToBillingHead();
        };

        const scrollToBillingHead = () => {
            nextTick(() => {
                window.requestAnimationFrame(() => {
                    const target = document.querySelector(".care-billing-page-shell .claim-registry-intro");

                    if (!target) {
                        return;
                    }

                    const offset = 150;
                    const top = target.getBoundingClientRect().top + window.pageYOffset - offset;

                    window.scrollTo({
                        top: Math.max(top, 0),
                        behavior: "smooth",
                    });
                });
            });
        };

        const resetBillingHomeScroll = () => {
            nextTick(() => {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        window.scrollTo({
                            top: 0,
                            behavior: "auto",
                        });
                    });
                });
            });
        };

        // get function
        const getItemsFromApi = (table, id) => {

            // get item from store (cache)
            let cache = getCache(`${table}/${id}`);

            if (cache?.length) {
                response.value = cache;
            }

            (async () => {
                const { res, err } = await getItemsOfItems(table, id);

                if (res.value) {
                    response.value = res.value.data;
                    // add cache
                    addCache(`${table}/${id}`, response.value);
                    // set isEmpty to false
                    // isEmpty.value = isEmpty.value && response.value.data.length ? false : true;
                } else {
                    error.value = err.value;
                }

                // page is ready, loading is complete
                isReady.value = true;
                isLoading.value = false;
            })();
        };

        watch(
            () => store.state.realtimeAuthEvent?.event_id,
            (eventId) => {
                if (!eventId || !currUrl.value || showStartPanel.value) {
                    return;
                }

                isLoading.value = true;
                getItemsFromApi(currUrl.value, `${providerId}${currHook.value}`);
                store.commit("dismissRealtimeAuthNotice", eventId);
            }
        );


        // handle search (emitted from component)
        const handleSearch = (val) => {
            // start loading
            isLoading.value = true;
            // set current Url
            currUrl.value = val.currUrl;
            // set current Hook
            currHook.value = val.currHook;
            // set search title
            searchTitle.value = val.searchTitle;
            // get items from function
            getItemsFromApi(currUrl.value, `${providerId}${currHook.value}`);
        };

        // handle edit
        const handleReview = (cluster) => {
            router.push({ name: "billingView", params: { clusterId: cluster.id } });
        };

        // switch page
        const handlePage = (page) => {
            // start laoding
            isLoading.value = true;
            // go back up
            router.push({ hash: "#top" });
            // get new page items from function
            getItemsFromApi(currUrl.value, `${providerId}${currHook.value}&page=${page}`);
            // kill update
            UIUpdate.value = false;
        };

        // output results
        const clusters = computed(() => {
            if (!response.value) {
                return [];
            } else {
                return response.value.data; // get data portion
            }
        });

        // output pages
        const allPages = computed(() => {
            if (!response.value) {
                return [];
            } else {
                return response.value; // get all
            }
        });

        return {
            handleActive,
            home,
            currTab,
            tabs,
            capWords,
            numberBadge,
            grossTotalSent,
            grossTotalApproved,
            toMonthYear,
            checkPaid,
            paymentStatus,
            focusBatchFilters,
            handlePrimaryStartAction,
            handleStartCancel,
            handleBatchStarted,
            handlePage,
            handleSearch,
            handleReview,
            hasManualBillingSchemes,
            isReady,
            isLoading,
            error,
            searchTitle,
            showSearchFilters,
            showStartPanel,
            clusters,
            allPages,
            HMO: HMO,
        };
    },
};
</script>

<style scoped>
@layer components {

.claim-page-shell {
  box-sizing: border-box !important;
  margin: 0 auto !important;
  max-width: var(--ui-content-width) !important;
  padding: 2rem 2rem 3rem !important;
  width: 100% !important;
}

.claim-workbench {
  color: var(--ui-foreground) !important;
  min-width: 0 !important;
}

.claim-registry-intro {
  border-bottom: 1px solid var(--ui-border) !important;
  margin-bottom: 2rem !important;
  padding-bottom: 1.75rem !important;
}

.claim-registry-intro :deep(.ui-page-header) {
  margin-bottom: 0 !important;
}

.claim-registry-intro :deep(.ui-page-header__title) {
  font-size: clamp(1.75rem, 2.1vw, 2rem) !important;
  font-weight: 700 !important;
  letter-spacing: -0.025em !important;
}

.claim-registry-intro :deep(.ui-page-header__description) {
  font-size: 1rem !important;
  line-height: 1.55 !important;
}

.claim-add-link {
  align-items: center !important;
  background: var(--ui-primary) !important;
  border: 1px solid var(--ui-primary) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  color: var(--ui-primary-foreground) !important;
  display: inline-flex !important;
  font-size: 0.8125rem !important;
  font-weight: 600 !important;
  gap: 0.5rem !important;
  justify-content: center !important;
  min-height: 2.5rem !important;
  padding: 0 0.9rem !important;
  text-decoration: none !important;
}

.claim-add-link:hover {
  filter: brightness(0.95) !important;
}

.claim-add-link:focus-visible {
  outline: 2px solid var(--ui-ring) !important;
  outline-offset: 2px !important;
}

.claim-registry-toolbar,
.claim-filter-shell,
.claim-search-summary,
.claim-cluster-section,
.claim-empty-state {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both !important;
}

.claim-registry-toolbar {
  align-items: center !important;
  background: var(--ui-background) !important;
  border: 0 !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex !important;
  gap: 1.5rem !important;
  justify-content: space-between !important;
  padding: 0 0 1.25rem !important;
}

.claim-review-summary {
  display: flex !important;
  flex-direction: column !important;
  gap: 0.15rem !important;
  min-width: 0 !important;
}

.claim-review-summary strong {
  color: var(--ui-foreground) !important;
  font-size: 1rem !important;
  font-weight: 650 !important;
  line-height: 1.4 !important;
}

.claim-review-summary span {
  color: var(--ui-muted-foreground) !important;
  font-size: 0.8125rem !important;
  line-height: 1.45 !important;
}

.claim-search-toggle {
  align-items: center !important;
  background: var(--ui-primary) !important;
  border: 1px solid var(--ui-primary) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  color: var(--ui-primary-foreground) !important;
  cursor: pointer !important;
  display: inline-flex !important;
  font-size: 0.8125rem !important;
  font-weight: 600 !important;
  gap: 0.5rem !important;
  justify-content: center !important;
  min-height: 2.5rem !important;
  padding: 0 0.9rem !important;
}

.claim-search-toggle i {
  font-size: 1rem !important;
}

.claim-search-toggle:focus-visible,
.claim-row-action:focus-visible {
  outline: 3px solid rgba(14, 116, 144, 0.24) !important;
  outline-offset: 2px !important;
}

.claim-filter-shell {
  background: var(--ui-background) !important;
  border-bottom: 1px solid var(--ui-border) !important;
  padding: 1.25rem 0 !important;
  position: relative !important;
  z-index: 5 !important;
}

.claim-filter-panel {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  overflow: visible !important;
  padding: 0 !important;
  width: 100% !important;
}

.claim-filter-panel :deep(.sectionx),
.claim-filter-panel :deep(form) {
  margin: 0 !important;
  padding: 0 !important;
}

.claim-filter-panel :deep(.sectionx > br) {
  display: none !important;
}

.claim-filter-panel :deep(.row) {
  align-items: start !important;
  display: grid !important;
  gap: 1rem !important;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  margin: 0 !important;
}

.claim-filter-panel :deep(.col) {
  border: 0 !important;
  min-width: 0 !important;
  padding: 0 !important;
  width: 100% !important;
}

.claim-filter-panel :deep(.col:focus-within) {
  position: relative !important;
  z-index: 3 !important;
}

.claim-filter-panel :deep(.col > label) {
  color: var(--ui-foreground) !important;
  display: block !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  line-height: 1.35 !important;
  margin-bottom: 0.4rem !important;
}

.claim-filter-panel :deep(nav),
.claim-filter-panel :deep(.nav-wrapper),
.claim-filter-panel :deep(.input-field) {
  background: transparent !important;
  box-shadow: none !important;
  height: auto !important;
  line-height: normal !important;
  margin: 0 !important;
  min-height: 2.75rem !important;
  overflow: visible !important;
  width: 100% !important;
}

.claim-filter-panel :deep(.input-field) {
  position: relative !important;
}

.claim-filter-panel :deep(.input-field > input),
.claim-filter-panel :deep(.select-wrapper input.select-dropdown) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  box-shadow: none !important;
  box-sizing: border-box !important;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  height: 2.75rem !important;
  line-height: 2.75rem !important;
  margin: 0 !important;
  padding: 0 2.5rem 0 0.75rem !important;
  width: 100% !important;
}

.claim-filter-panel :deep(.input-field > input[type="search"]::-webkit-search-cancel-button) {
  -webkit-appearance: none !important;
  display: none !important;
}

.claim-filter-panel :deep(.input-field > input:focus) {
  border-color: var(--ui-ring) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ui-ring) 18%, transparent) !important;
}

.claim-filter-panel :deep(.input-field > .material-icons) {
  align-items: center !important;
  background: transparent !important;
  border: 0 !important;
  color: var(--ui-muted-foreground) !important;
  cursor: pointer !important;
  display: inline-flex !important;
  font-size: 1.1rem !important;
  height: 2.75rem !important;
  justify-content: center !important;
  line-height: 1 !important;
  margin: 0 !important;
  position: absolute !important;
  right: 0.25rem !important;
  text-decoration: none !important;
  top: 0 !important;
  width: 2.25rem !important;
  z-index: 2 !important;
}

.claim-filter-panel :deep(.input-field > .material-icons:hover) {
  color: var(--ui-foreground) !important;
}

.claim-filter-panel :deep(.native-autocomplete) {
  background: var(--ui-popover) !important;
  border: 1px solid var(--ui-border) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  box-shadow: var(--ui-shadow-overlay) !important;
  box-sizing: border-box !important;
  left: 0 !important;
  margin: 0 !important;
  max-height: min(16rem, 50vh) !important;
  min-width: 0 !important;
  overflow-y: auto !important;
  padding: 0.3rem !important;
  right: 0 !important;
  top: calc(100% + 0.25rem) !important;
  width: 100% !important;
  z-index: 220 !important;
}

.claim-filter-panel :deep(.native-autocomplete li) {
  margin: 0 !important;
  min-height: 0 !important;
}

.claim-filter-panel :deep(.native-autocomplete li > :is(button, span)) {
  align-items: center !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: calc(var(--ui-radius) - 4px) !important;
  color: var(--ui-popover-foreground) !important;
  cursor: pointer !important;
  display: flex !important;
  font-size: 0.8125rem !important;
  font-weight: 400 !important;
  line-height: 1.35 !important;
  min-height: 2.35rem !important;
  padding: 0.55rem 0.65rem !important;
  text-align: left !important;
  width: 100% !important;
}

.claim-filter-panel :deep(.native-autocomplete li > button:hover),
.claim-filter-panel :deep(.native-autocomplete li > button:focus-visible),
.claim-filter-panel :deep(.native-autocomplete li > button[aria-selected="true"]) {
  background: var(--ui-accent) !important;
  color: var(--ui-accent-foreground) !important;
  outline: 0 !important;
}

.claim-filter-panel :deep(.mobile-remote-sheet-field) {
  min-height: 2.75rem !important;
}

.claim-filter-panel :deep(.mobile-remote-trigger) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  height: 2.75rem !important;
  line-height: normal !important;
  padding: 0 0.25rem 0 0.75rem !important;
}

.claim-filter-panel :deep(.mobile-remote-trigger-main),
.claim-filter-panel :deep(.mobile-remote-trigger-icon) {
  height: 2.65rem !important;
  line-height: normal !important;
}

.claim-filter-panel :deep(.dropdown-content) {
  background: #ffffff !important;
  border: 1px solid #d7e1e7 !important;
  border-radius: 0 0 6px 6px !important;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12) !important;
  left: 0 !important;
  margin: -1px 0 0 !important;
  max-height: 240px !important;
  overflow-y: auto !important;
  position: absolute !important;
  top: 100% !important;
  transform: none !important;
  width: 100% !important;
  z-index: 30 !important;
}

.claim-filter-panel :deep(.dropdown-content li) {
  min-height: 42px !important;
}

.claim-filter-panel :deep(.dropdown-content li > span) {
  color: #172033 !important;
  font-size: 0.9rem !important;
  line-height: 1.35 !important;
  padding: 11px 15px !important;
}

.claim-search-summary {
  align-items: center !important;
  background: var(--ui-background) !important;
  border: 0 !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-radius: 0 !important;
  display: flex !important;
  gap: 1rem !important;
  justify-content: space-between !important;
  min-height: 3.5rem !important;
  padding: 0.75rem 0 !important;
}

.summary-label {
  color: var(--ui-muted-foreground) !important;
  flex: 0 0 auto !important;
  font-size: 0.6875rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em !important;
  text-transform: uppercase !important;
}

.summary-content {
  align-items: center !important;
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 0.4rem !important;
  justify-content: flex-end !important;
  min-width: 0 !important;
  text-align: right !important;
}

.claim-search-summary :deep(.chip),
.summary-chip {
  background: var(--ui-secondary) !important;
  border: 1px solid var(--ui-border) !important;
  border-radius: 999px !important;
  color: var(--ui-secondary-foreground) !important;
  font-size: 0.75rem !important;
  font-style: normal !important;
  font-weight: 500 !important;
  height: auto !important;
  line-height: 1.25 !important;
  margin: 0 !important;
  padding: 0.35rem 0.65rem !important;
  white-space: nowrap !important;
}

.claim-search-summary :deep(.chip:first-child) {
  background: transparent !important;
  color: var(--ui-muted-foreground) !important;
}

.claim-search-summary :deep(.chip i) {
  font-size: 0.8rem !important;
  line-height: 1 !important;
}

.claim-cluster-section {
  margin-top: 0 !important;
}

.claim-cluster-list {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  overflow: visible !important;
  position: relative !important;
}

.claim-list-progress {
  background: #e5edf1 !important;
  height: 2px !important;
  left: 0 !important;
  overflow: hidden !important;
  position: absolute !important;
  right: 0 !important;
  top: 0 !important;
  z-index: 2 !important;
}

.claim-list-progress span {
  animation: claimListLoading 1.1s ease-in-out infinite !important;
  background: #0f766e !important;
  display: block !important;
  height: 100% !important;
  width: 36% !important;
}

@keyframes claimListLoading {
  from {
    transform: translateX(-105%);
  }

  to {
    transform: translateX(285%);
  }
}

.claim-cluster-head,
.claim-cluster-row {
  align-items: center !important;
  display: grid !important;
  gap: 1rem !important;
  grid-template-columns: 2.5rem minmax(14rem, 1.7fr) minmax(9rem, 1fr) minmax(10rem, 1.1fr) minmax(7rem, 0.75fr) 8.25rem 2.75rem !important;
}

.claim-cluster-head {
  background: transparent !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-top: 0 !important;
  color: var(--ui-muted-foreground) !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0.8rem 0.75rem !important;
  text-transform: uppercase !important;
}

.claim-cluster-head > span {
  padding: 0 !important;
}

.claim-cluster-row {
  background: transparent !important;
  border: 0 !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 1.15rem 0.75rem !important;
  transition: background-color 160ms ease !important;
}

.claim-cluster-row:hover,
.claim-cluster-row:focus-within {
  background: color-mix(in srgb, var(--ui-muted) 45%, transparent) !important;
}

.claim-cluster-row > div {
  min-width: 0 !important;
  padding: 0 !important;
}

.claim-row-number {
  color: var(--ui-muted-foreground) !important;
  font-size: 0.8125rem !important;
  font-weight: 600 !important;
  text-align: left !important;
}

.claim-provider-title {
  align-items: flex-start !important;
  color: var(--ui-foreground) !important;
  display: flex !important;
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  gap: 0.5rem !important;
  line-height: 1.4 !important;
}

.claim-flags {
  color: #e11d48 !important;
  display: inline-flex !important;
  flex: 0 0 auto !important;
}

.claim-flags i {
  font-size: 17px !important;
}

.claim-count-text {
  color: var(--ui-muted-foreground) !important;
  display: block !important;
  font-size: 0.75rem !important;
  line-height: 1.4 !important;
  margin-top: 0.3rem !important;
}

.claim-related-badges {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 5px !important;
  margin-top: 7px !important;
}

.claim-related-badge {
  border: 1px solid #d7e1e7 !important;
  border-radius: 999px !important;
  color: #52677a !important;
  display: inline-flex !important;
  font-size: 0.64rem !important;
  font-weight: 800 !important;
  line-height: 1 !important;
  padding: 4px 7px !important;
  text-transform: uppercase !important;
}

.claim-related-badge.is-supplementary {
  background: #f0fdf4 !important;
  border-color: #bbddc7 !important;
  color: #18703d !important;
}

.claim-related-badge.is-reconciliation {
  background: #eff6ff !important;
  border-color: #c5d9f2 !important;
  color: #245c94 !important;
}

.claim-data-cell,
.claim-period-cell {
  color: var(--ui-foreground) !important;
  font-size: 0.8125rem !important;
  line-height: 1.4 !important;
}

.claim-amount {
  border: 1px solid !important;
  border-radius: 999px !important;
  display: inline-flex !important;
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  padding: 0.35rem 0.55rem !important;
  white-space: nowrap !important;
}

.claim-amount.is-sent {
  background: #fff0f3 !important;
  border-color: #f3b4c0 !important;
  color: #ed1231 !important;
}

.claim-amount.is-approved {
  background: #eaf8f2 !important;
  border-color: #b6dacb !important;
  color: #008442 !important;
}

.claim-muted-value {
  color: var(--ui-muted-foreground) !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
}

.claim-period-cell strong,
.claim-period-cell > span:last-child {
  display: block !important;
}

.claim-period-cell strong {
  color: var(--ui-foreground) !important;
  font-weight: 600 !important;
}

.claim-period-cell > span:last-child {
  color: var(--ui-muted-foreground) !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  margin-top: 0.25rem !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.claim-status {
  background: #f1f5f9 !important;
  border: 1px solid #d7e1e7 !important;
  border-radius: 999px !important;
  color: #52677a !important;
  display: inline-flex !important;
  font-size: 0.6875rem !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  padding: 0.35rem 0.55rem !important;
  text-transform: uppercase !important;
  white-space: nowrap !important;
}

.claim-status.is-paid {
  background: #eaf8f2 !important;
  border-color: #b6dacb !important;
  color: #008442 !important;
}

.claim-status.is-pending {
  background: #fff8e6 !important;
  border-color: #ecd9a0 !important;
  color: #855500 !important;
}

.claim-status.is-unsubmitted {
  background: #fff4e8 !important;
  border-color: #edc69d !important;
  color: #9a4e06 !important;
}

.claim-action-cell {
  display: flex !important;
  justify-content: center !important;
}

.claim-row-action {
  align-items: center !important;
  background: #ffffff !important;
  border: 1px solid #d7e1e7 !important;
  border-radius: 6px !important;
  color: #52677a !important;
  cursor: pointer !important;
  display: inline-flex !important;
  height: 2.5rem !important;
  justify-content: center !important;
  padding: 0 !important;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease !important;
  width: 2.5rem !important;
}

.claim-cluster-row:hover .claim-row-action:not(:disabled),
.claim-cluster-row:focus-within .claim-row-action:not(:disabled) {
  background: #0f172a !important;
  border-color: #0f172a !important;
  color: #ffffff !important;
}

.claim-row-action i {
  font-size: 20px !important;
  text-decoration: none !important;
}

.claim-row-action.is-locked {
  background: #f8fafc !important;
  color: #94a3b8 !important;
  cursor: default !important;
}

.claim-mobile-label {
  display: none !important;
}

.claim-empty-state {
  align-items: center !important;
  background: #ffffff !important;
  border: 1px solid #dbe4ea !important;
  border-radius: 7px !important;
  color: #64748b !important;
  display: flex !important;
  gap: 17px !important;
  margin-top: 18px !important;
  min-height: 150px !important;
  padding: 28px 30px !important;
}

.claim-empty-icon {
  align-items: center !important;
  background: #eef8f7 !important;
  border: 1px solid #cce5e2 !important;
  border-radius: 7px !important;
  color: #0f766e !important;
  display: inline-flex !important;
  flex: 0 0 auto !important;
  height: 52px !important;
  justify-content: center !important;
  width: 52px !important;
}

.claim-empty-icon i {
  font-size: 26px !important;
}

.claim-empty-copy h6 {
  color: #172033 !important;
  font-size: 1rem !important;
  font-weight: 800 !important;
  margin: 0 !important;
}

.claim-empty-copy p {
  color: #64748b !important;
  line-height: 1.45 !important;
  margin: 6px 0 0 !important;
}

.claim-empty-copy span {
  color: #8291a3 !important;
  display: block !important;
  font-size: 0.8rem !important;
  font-weight: 650 !important;
  margin-top: 8px !important;
}

.claim-pagination {
  display: block !important;
  margin-top: 24px !important;
}

.pointer,
.pointable {
  cursor: default !important;
}

@media only screen and (max-width: 1100px) {
  .claim-cluster-head,
  .claim-cluster-row {
    gap: 0.75rem !important;
    grid-template-columns: 2rem minmax(12rem, 1.5fr) minmax(8rem, 0.9fr) minmax(9rem, 1fr) minmax(6rem, 0.7fr) 7.75rem 2.5rem !important;
  }
}

@media only screen and (max-width: 992px) {
  .claim-page-shell {
    padding-inline: 1.25rem !important;
  }

  .claim-filter-panel :deep(.row) {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .claim-cluster-head {
    display: none !important;
  }

  .claim-cluster-list {
    border-bottom: 0 !important;
  }

  .claim-cluster-row {
    gap: 12px 24px !important;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
    min-height: 0 !important;
    padding: 20px 64px 20px 22px !important;
    position: relative !important;
  }

  .claim-cluster-row > div {
    padding: 0 !important;
  }

  .claim-row-number {
    display: none !important;
  }

  .claim-provider-cell {
    grid-column: 1 / -1 !important;
  }

  .claim-action-cell {
    position: absolute !important;
    right: 16px !important;
    top: 20px !important;
  }

  .claim-mobile-label {
    color: #8291a3 !important;
    display: block !important;
    font-size: 0.66rem !important;
    font-weight: 800 !important;
    letter-spacing: 0.04em !important;
    margin-bottom: 6px !important;
    text-transform: uppercase !important;
  }
}

@media only screen and (max-width: 600px) {
  .claim-page-shell {
    padding: 1.25rem 1rem 2.5rem !important;
  }

  .claim-registry-intro {
    margin-bottom: 1.5rem !important;
    padding-bottom: 1.25rem !important;
  }

  .claim-registry-toolbar {
    align-items: stretch !important;
    flex-direction: column !important;
  }

  .claim-search-toggle {
    width: 100% !important;
  }

  .claim-filter-shell {
    padding: 1.25rem 0 !important;
  }

  .claim-filter-panel :deep(.row) {
    grid-template-columns: minmax(0, 1fr) !important;
  }

  .claim-search-summary {
    align-items: flex-start !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  .summary-content {
    justify-content: flex-start !important;
    text-align: left !important;
  }

  .claim-cluster-row {
    gap: 16px !important;
    grid-template-columns: minmax(0, 1fr) !important;
    padding: 20px 62px 20px 18px !important;
  }

  .claim-provider-cell {
    grid-column: auto !important;
  }

  .claim-empty-state {
    align-items: flex-start !important;
    flex-direction: column !important;
    padding: 24px 20px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .claim-registry-toolbar,
  .claim-filter-shell,
  .claim-search-summary,
  .claim-cluster-section,
  .claim-empty-state {
    animation: none !important;
  }

  .claim-list-progress span {
    animation: none !important;
    width: 100% !important;
  }
}

.billing-start-action { cursor: pointer !important; font-family: inherit !important; }
.billing-start-action i { font-size: 17px !important; }
.billing-start-action:disabled { cursor: not-allowed !important; opacity: .5 !important; }
.billing-start-action.is-cancel { background: var(--ui-background) !important; border-color: var(--ui-border) !important; color: var(--ui-foreground) !important; }
.claim-filter-panel :deep(.billing-filter-controls) { display: grid !important; grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 1rem !important; width: 100% !important; }
.claim-filter-panel :deep(.billing-filter-controls > label) { display: grid !important; gap: .4rem !important; min-width: 0 !important; }
.claim-filter-panel :deep(.billing-filter-controls > label > span) { font-size: .75rem !important; font-weight: 600 !important; line-height: 1.35 !important; color: var(--ui-foreground) !important; }
.claim-filter-panel :deep(.billing-filter-controls select) { background: var(--ui-background) !important; border: 1px solid var(--ui-input) !important; border-radius: calc(var(--ui-radius) - 2px) !important; box-shadow: none !important; color: var(--ui-foreground) !important; font-size: .875rem !important; font-weight: 400 !important; height: 2.75rem !important; margin: 0 !important; padding: 0 .75rem !important; width: 100% !important; }
@media (max-width: 992px) { .claim-filter-panel :deep(.billing-filter-controls) { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
@media (max-width: 600px) { .claim-filter-panel :deep(.billing-filter-controls) { grid-template-columns: minmax(0, 1fr) !important; } }

}
</style>
